import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Warning, Heart, Drop, Pill } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const KnowYourSubstancePage = () => {
  const navigate = useNavigate();
  const [selectedSubstance, setSelectedSubstance] = useState(null);

  const substances = [
    {
      id: 'crystal-meth',
      name: 'Crystal Meth (Tina)',
      emoji: '💎',
      color: 'from-blue-500 to-cyan-500',
      likes: [
        'Energy',
        'Confidence',
        'Sex feels intense',
        'Staying awake longer'
      ],
      risks: [
        'Anxiety, paranoia',
        'Crash after',
        'Sleep loss',
        'Dehydration',
        'Mental health impact',
        'Risk of dependency'
      ],
      partySmartTips: [
        'Drink water (small sips often)',
        'Eat before & after',
        'Take breaks',
        'Don\'t mix with too many substances',
        'Sleep after the party',
        'Be gentle with yourself after'
      ],
      afterCare: {
        title: 'Tina Post-Smoke Self-Care',
        steps: [
          {
            title: 'Clean Your Mouth + Throat',
            items: ['Brush teeth - Clear meth residue & bacteria', 'Scrape tongue gently', 'Gargle with warm salt water', 'Spit out mucus']
          },
          {
            title: 'Open Up Lungs & Nose',
            items: ['Hot shower or steam bowl', 'Breathe in steam (5-10 min)', 'Use saline spray or neti pot']
          },
          {
            title: 'Soothe That Burn',
            items: ['Tea time: Ginger + lemon + honey', 'Chamomile or marshmallow root', 'Skip cold drinks, go warm', 'Use lip balm for dry lips']
          },
          {
            title: 'Kick Out the Mucus',
            items: ['Guaifenesin (Robitussin)', 'NAC supplement', 'Vitamin C (500-1000mg)', 'Magnesium (200-400mg)']
          }
        ]
      }
    },
    {
      id: 'ghb-gbl',
      name: 'GHB / GBL / BDO',
      emoji: '🧪',
      color: 'from-purple-500 to-pink-500',
      likes: [
        'Relaxation',
        'Confidence',
        'Sex feels smoother',
        'Less anxiety (at first)'
      ],
      risks: [
        'Easy to overdose',
        'Memory loss',
        'Blackouts',
        'Breathing slows',
        'Dangerous with alcohol'
      ],
      partySmartTips: [
        'Measure doses carefully',
        'Use a timer (never redose early)',
        'Avoid mixing with alcohol or benzos',
        'Don\'t use alone',
        'If someone won\'t wake up, get help immediately'
      ],
      types: [
        {
          name: 'BDO (Butanediol)',
          desc: 'Oily/thicker, slower to hit, can sneak up on you',
          dose: '2.5ml starting (some go to 3.0ml)'
        },
        {
          name: 'GBL (Gamma-Butyrolactone)',
          desc: 'Watery, can sting mouth, hits faster, easier to overdo',
          dose: '2.0ml standard (experienced: 1.5ml)'
        },
        {
          name: 'GHB (Gamma-Hydroxybutyrate)',
          desc: 'Watery, salty taste, strength varies, unpredictable',
          dose: 'Consult source - strength unknown'
        }
      ]
    },
    {
      id: 'mdma',
      name: 'MDMA (Molly / Ecstasy)',
      emoji: '💗',
      color: 'from-pink-500 to-rose-500',
      likes: [
        'Love, connection',
        'Empathy',
        'Touch feels amazing',
        'Emotional openness'
      ],
      risks: [
        'Dehydration or overheating',
        'Jaw clenching',
        'Low mood after',
        'Sleep issues',
        'Serotonin crash'
      ],
      partySmartTips: [
        'Drink water',
        'Take breaks from dancing/sex',
        'Avoid mixing with antidepressants',
        'Eat before & after',
        'Rest for a few days after'
      ]
    },
    {
      id: 'ketamine',
      name: 'Ketamine',
      emoji: '🌀',
      color: 'from-indigo-500 to-purple-500',
      likes: [
        'Dissociation',
        'Calm or floaty feeling',
        'Less emotional pain',
        'Sex feels different'
      ],
      risks: [
        'Loss of balance',
        'Confusion',
        'Bladder issues (with frequent use)',
        'Nausea',
        'Accidents or falls'
      ],
      partySmartTips: [
        'Sit or lie down',
        'Go slow with dosing',
        'Drink water',
        'Take breaks',
        'Avoid mixing with alcohol'
      ]
    },
    {
      id: 'cocaine',
      name: 'Cocaine',
      emoji: '⚡',
      color: 'from-yellow-500 to-orange-500',
      likes: [
        'Energy',
        'Confidence',
        'Focus',
        'Feeling "up"'
      ],
      risks: [
        'Anxiety, paranoia',
        'Heart strain',
        'Nose damage',
        'Strong crash',
        'Mixing risks'
      ],
      partySmartTips: [
        'Take breaks',
        'Avoid frequent redosing',
        'Rinse nose after',
        'Don\'t mix with alcohol (creates toxic combo)',
        'Watch your heart & anxiety'
      ]
    },
    {
      id: 'alcohol',
      name: 'Alcohol',
      emoji: '🍷',
      color: 'from-amber-500 to-red-500',
      likes: [
        'Social confidence',
        'Relaxation',
        'Easy access'
      ],
      risks: [
        'Dehydration',
        'Poor judgment',
        'Increased HIV/STBBI risk',
        'Dangerous mixing',
        'Hangovers & depression'
      ],
      partySmartTips: [
        'Alternate with water',
        'Eat first',
        'Set and know your limit',
        'Avoid mixing with G or ketamine',
        'Check in with your mood after'
      ]
    },
    {
      id: 'poppers',
      name: 'Poppers',
      emoji: '💨',
      color: 'from-cyan-500 to-blue-500',
      likes: [
        'Muscle relaxation',
        'Stronger orgasms',
        'Short, intense rush'
      ],
      risks: [
        'Headaches',
        'Dizziness',
        'Blood pressure drop',
        'Dangerous with ED meds'
      ],
      partySmartTips: [
        'Take small inhales',
        'Sit or lie down',
        'Avoid frequent use',
        'NEVER mix with ED pills',
        'Stop if you feel faint'
      ]
    },
    {
      id: 'ed-pills',
      name: 'ED Pills (Viagra, Cialis)',
      emoji: '💊',
      color: 'from-blue-400 to-indigo-500',
      likes: [
        'Stronger erections',
        'Longer sex sessions',
        'Confidence boost'
      ],
      risks: [
        'Headaches',
        'Heart strain',
        'Low blood pressure',
        'Dangerous drug interactions'
      ],
      partySmartTips: [
        'Avoid mixing with poppers',
        'Avoid mixing with heavy stimulants',
        'Use lowest effective dose',
        'Stay hydrated',
        'If chest pain happens, get help'
      ]
    },
    {
      id: 'cannabis',
      name: 'Cannabis',
      emoji: '🌿',
      color: 'from-green-500 to-emerald-500',
      likes: [
        'Pain relief',
        'Appetite boost',
        'Relaxation',
        'Creativity'
      ],
      risks: [
        'Smoking harms lungs over time',
        'Can affect memory/focus',
        'Anxiety in some people',
        'Impaired coordination'
      ],
      partySmartTips: [
        'Vaping or edibles are safer than smoking',
        'Start with small dose',
        'Avoid mixing with alcohol',
        'Stay hydrated',
        'Have snacks ready'
      ]
    }
  ];

  const drugTesting = {
    title: 'Drug Testing & Fentanyl Safety',
    emoji: '🧬',
    warning: 'Fentanyl has been found in many street drugs, even Tina. You cannot see it, smell it, or taste it. Testing can save lives.',
    methods: [
      {
        name: 'Fentanyl Test Strips',
        desc: 'Dissolve small amount in water, dip strip, wait 5 minutes. Read results per instructions.',
        where: 'Order online (Amazon)'
      },
      {
        name: 'Reagent Kits',
        desc: 'Liquid reagents test for various substances through color changes.',
        where: 'Harm reduction programs'
      },
      {
        name: 'Lab Drug Checking',
        desc: 'Most accurate. Send sample to testing service.',
        where: 'Sandy Hill CHC, Ottawa Inner City Health CTS'
      }
    ],
    overdoseSigns: [
      'Slow or no breathing',
      'Very hard to wake up',
      'Blue lips or fingertips',
      'Cold, clammy skin',
      'Pinpoint pupils',
      'Confusion or collapsing'
    ],
    response: [
      'Stay calm. Act fast.',
      'Give Naloxone (Narcan) - nasal spray or injection',
      'Call 911 immediately',
      'Support breathing if trained',
      'Stay with the person until help arrives',
      'NEVER leave someone alone'
    ]
  };

  const postPartyCare = {
    title: 'Post-Party Self-Care',
    emoji: '🛁',
    steps: [
      {
        title: 'Hydrate First',
        desc: 'Drink water slowly. Electrolytes (sports drinks) help replace what you lost.',
        icon: '💧'
      },
      {
        title: 'Eat Something Nourishing',
        desc: 'Fruit, eggs, smoothies, toast with peanut butter. Focus on nutrients.',
        icon: '🍎'
      },
      {
        title: 'Rest & Sleep',
        desc: 'Sleep helps your brain repair. Short naps okay. Melatonin can help.',
        icon: '😴'
      },
      {
        title: 'Mind Your Mental Health',
        desc: 'Anxiety, low mood, racing thoughts are common. Journal, breathe, talk to someone.',
        icon: '🧠'
      },
      {
        title: 'Gentle Body Care',
        desc: 'Stretch, shower, massage tense areas. Feeling clean helps your mind reset.',
        icon: '🚿'
      },
      {
        title: 'Reflect & Plan',
        desc: 'What went well? What could be safer next time? Small adjustments prevent big problems.',
        icon: '✨'
      }
    ],
    checklist: [
      'Water & electrolytes',
      'Nutritious food',
      'Rest or sleep',
      'Mental check-in',
      'Gentle movement & hygiene',
      'Monitor for health concerns'
    ]
  };

  return (
    <div className="min-h-screen flex flex-col relative z-10">
      <header className="glass-morphism sticky top-0 z-50 border-b">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => selectedSubstance ? setSelectedSubstance(null) : navigate('/')}
              data-testid="back-button"
              className="mr-4 text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--primary))]"
            >
              <ArrowLeft size={24} weight="duotone" />
            </Button>
            <div className="flex items-center space-x-2">
              <span className="text-3xl emoji-bounce">💊</span>
              <h1 className="text-2xl font-bold text-gradient">
                {selectedSubstance === 'testing' ? 'Drug Testing' : selectedSubstance === 'aftercare' ? 'Post-Party Care' : selectedSubstance ? substances.find(s => s.id === selectedSubstance)?.name : 'Know Your Substance'}
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-8 pb-24">
        {!selectedSubstance ? (
          <>
            <div className="mb-8 p-6 party-gradient rounded-3xl text-white text-center">
              <div className="text-5xl mb-3">🧬</div>
              <h2 className="text-2xl font-bold mb-3">Know What You're Using</h2>
              <p className="text-white/90 leading-relaxed">
                Real info about substances. What people like, what can be hard, and how to party smarter. No judgment, just facts. 💜
              </p>
            </div>

            {/* Drug Testing Card */}
            <button
              onClick={() => setSelectedSubstance('testing')}
              data-testid="substance-testing"
              className="w-full mb-4 group relative bg-gradient-to-br from-red-500/20 to-orange-500/20 hover:from-red-500/30 hover:to-orange-500/30 rounded-3xl p-6 border border-red-500/30 text-left transition-all duration-200 btn-hover"
            >
              <div className="flex items-center">
                <div className="text-5xl mr-4">🧬</div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[rgb(var(--text-primary))] mb-1">
                    Drug Testing & Fentanyl Safety
                  </h3>
                  <p className="text-sm text-[rgb(var(--text-secondary))]">
                    Test. Don't Guess. Stay Alive.
                  </p>
                </div>
              </div>
            </button>

            {/* Post-Party Care Card */}
            <button
              onClick={() => setSelectedSubstance('aftercare')}
              data-testid="substance-aftercare"
              className="w-full mb-6 group relative bg-gradient-to-br from-green-500/20 to-emerald-500/20 hover:from-green-500/30 hover:to-emerald-500/30 rounded-3xl p-6 border border-green-500/30 text-left transition-all duration-200 btn-hover"
            >
              <div className="flex items-center">
                <div className="text-5xl mr-4">🛁</div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[rgb(var(--text-primary))] mb-1">
                    Post-Party Self-Care
                  </h3>
                  <p className="text-sm text-[rgb(var(--text-secondary))]">
                    Recovery isn't weak, it's smart
                  </p>
                </div>
              </div>
            </button>

            <h3 className="text-lg font-bold text-[rgb(var(--text-primary))] mb-4">Substances A-Z</h3>
            <div className="grid grid-cols-2 gap-4">
              {substances.map((substance) => (
                <button
                  key={substance.id}
                  onClick={() => setSelectedSubstance(substance.id)}
                  data-testid={`substance-${substance.id}`}
                  className="group relative bg-[rgb(var(--surface))] hover:bg-[rgb(var(--surface-elevated))] rounded-3xl p-6 border border-white/10 text-center transition-all duration-200 hover:border-[rgb(var(--primary))]/50 btn-hover overflow-hidden"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${substance.color} opacity-0 group-hover:opacity-10 transition-opacity`}></div>
                  <div className="relative z-10">
                    <div className="text-5xl mb-3 emoji-bounce">{substance.emoji}</div>
                    <h4 className="font-bold text-[rgb(var(--text-primary))] text-sm">
                      {substance.name}
                    </h4>
                  </div>
                </button>
              ))}
            </div>
          </>
        ) : selectedSubstance === 'testing' ? (
          <DrugTestingContent data={drugTesting} />
        ) : selectedSubstance === 'aftercare' ? (
          <PostPartyCareContent data={postPartyCare} />
        ) : (
          <SubstanceDetail substance={substances.find(s => s.id === selectedSubstance)} />
        )}
      </main>
    </div>
  );
};

const SubstanceDetail = ({ substance }) => (
  <div className="space-y-6">
    <div className={`p-6 bg-gradient-to-br ${substance.color} rounded-3xl text-white text-center`}>
      <div className="text-6xl mb-3">{substance.emoji}</div>
      <h2 className="text-3xl font-bold">{substance.name}</h2>
    </div>

    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-bold text-[rgb(var(--text-primary))] mb-3 flex items-center">
          <span className="text-2xl mr-2">✨</span>
          What People Like
        </h3>
        <ul className="space-y-2">
          {substance.likes.map((item, idx) => (
            <li key={idx} className="flex items-start p-3 bg-[rgb(var(--surface))] rounded-xl">
              <span className="text-[rgb(var(--success))] mr-2">•</span>
              <span className="text-[rgb(var(--text-secondary))]">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h3 className="text-xl font-bold text-[rgb(var(--text-primary))] mb-3 flex items-center">
          <span className="text-2xl mr-2">⚠️</span>
          What Can Be Hard
        </h3>
        <ul className="space-y-2">
          {substance.risks.map((item, idx) => (
            <li key={idx} className="flex items-start p-3 bg-[rgb(var(--danger))]/10 border border-[rgb(var(--danger))]/20 rounded-xl">
              <span className="text-[rgb(var(--danger))] mr-2">•</span>
              <span className="text-[rgb(var(--text-secondary))]">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      {substance.types && (
        <div>
          <h3 className="text-xl font-bold text-[rgb(var(--text-primary))] mb-3">Know Your Type</h3>
          {substance.types.map((type, idx) => (
            <div key={idx} className="mb-4 p-4 bg-[rgb(var(--surface))] rounded-2xl border border-white/10">
              <h4 className="font-bold text-[rgb(var(--text-primary))] mb-2">{type.name}</h4>
              <p className="text-sm text-[rgb(var(--text-secondary))] mb-2">{type.desc}</p>
              <p className="text-xs text-[rgb(var(--primary))] font-semibold">Starting dose: {type.dose}</p>
            </div>
          ))}
        </div>
      )}

      <div>
        <h3 className="text-xl font-bold text-[rgb(var(--text-primary))] mb-3 flex items-center">
          <span className="text-2xl mr-2">💜</span>
          Party Smart Tips
        </h3>
        <ul className="space-y-2">
          {substance.partySmartTips.map((tip, idx) => (
            <li key={idx} className="flex items-start p-3 bg-[rgb(var(--primary))]/10 border border-[rgb(var(--primary))]/20 rounded-xl">
              <span className="text-[rgb(var(--primary))] mr-2">✓</span>
              <span className="text-[rgb(var(--text-secondary))]">{tip}</span>
            </li>
          ))}
        </ul>
      </div>

      {substance.afterCare && (
        <div className="p-6 bg-gradient-to-r from-[rgb(var(--primary))]/10 to-[rgb(var(--secondary))]/10 rounded-3xl border border-[rgb(var(--primary))]/20">
          <h3 className="text-xl font-bold text-[rgb(var(--text-primary))] mb-4">{substance.afterCare.title}</h3>
          {substance.afterCare.steps.map((step, idx) => (
            <div key={idx} className="mb-4">
              <h4 className="font-semibold text-[rgb(var(--text-primary))] mb-2">{step.title}</h4>
              <ul className="space-y-1">
                {step.items.map((item, i) => (
                  <li key={i} className="text-sm text-[rgb(var(--text-secondary))] ml-4">• {item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

const DrugTestingContent = ({ data }) => (
  <div className="space-y-6">
    <div className="p-6 bg-gradient-to-br from-red-500 to-orange-500 rounded-3xl text-white">
      <div className="text-5xl mb-3 text-center">{data.emoji}</div>
      <h2 className="text-2xl font-bold mb-3 text-center">{data.title}</h2>
      <p className="text-white/90 leading-relaxed">{data.warning}</p>
    </div>

    <div>
      <h3 className="text-xl font-bold text-[rgb(var(--text-primary))] mb-4">Testing Methods</h3>
      {data.methods.map((method, idx) => (
        <div key={idx} className="mb-4 p-4 bg-[rgb(var(--surface))] rounded-2xl border border-white/10">
          <h4 className="font-bold text-[rgb(var(--text-primary))] mb-2">{method.name}</h4>
          <p className="text-sm text-[rgb(var(--text-secondary))] mb-2">{method.desc}</p>
          <p className="text-xs text-[rgb(var(--primary))]">Where: {method.where}</p>
        </div>
      ))}
    </div>

    <div className="p-6 bg-[rgb(var(--danger))]/10 border border-[rgb(var(--danger))]/30 rounded-3xl">
      <h3 className="text-xl font-bold text-[rgb(var(--danger))] mb-3">Overdose Signs</h3>
      <ul className="space-y-2">
        {data.overdoseSigns.map((sign, idx) => (
          <li key={idx} className="flex items-start">
            <Warning size={20} className="text-[rgb(var(--danger))] mr-2 mt-0.5 flex-shrink-0" />
            <span className="text-[rgb(var(--text-secondary))]">{sign}</span>
          </li>
        ))}
      </ul>
    </div>

    <div className="p-6 bg-gradient-to-r from-[rgb(var(--success))]/20 to-[rgb(var(--primary))]/20 rounded-3xl border border-[rgb(var(--success))]/30">
      <h3 className="text-xl font-bold text-[rgb(var(--text-primary))] mb-3">What To Do</h3>
      <ol className="space-y-3">
        {data.response.map((step, idx) => (
          <li key={idx} className="flex items-start">
            <span className="text-[rgb(var(--success))] font-bold mr-3 text-lg">{idx + 1}.</span>
            <span className="text-[rgb(var(--text-secondary))]">{step}</span>
          </li>
        ))}
      </ol>
    </div>
  </div>
);

const PostPartyCareContent = ({ data }) => (
  <div className="space-y-6">
    <div className="p-6 party-gradient rounded-3xl text-white text-center">
      <div className="text-5xl mb-3">{data.emoji}</div>
      <h2 className="text-2xl font-bold mb-3">{data.title}</h2>
      <p className="text-white/90">Recovery isn't weak, it's smart. Every step protects your body, mind, and community. 💜</p>
    </div>

    {data.steps.map((step, idx) => (
      <div key={idx} className="p-5 bg-[rgb(var(--surface))] rounded-2xl border border-white/10">
        <div className="flex items-start">
          <div className="text-4xl mr-4">{step.icon}</div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-[rgb(var(--text-primary))] mb-2">{step.title}</h3>
            <p className="text-[rgb(var(--text-secondary))] leading-relaxed">{step.desc}</p>
          </div>
        </div>
      </div>
    ))}

    <div className="p-6 bg-gradient-to-r from-[rgb(var(--primary))]/10 to-[rgb(var(--secondary))]/10 rounded-3xl border border-[rgb(var(--primary))]/20">
      <h3 className="text-lg font-bold text-[rgb(var(--text-primary))] mb-4">Quick Recovery Checklist</h3>
      <div className="grid grid-cols-2 gap-3">
        {data.checklist.map((item, idx) => (
          <div key={idx} className="flex items-center p-3 bg-[rgb(var(--surface))] rounded-xl">
            <Heart size={16} className="text-[rgb(var(--primary))] mr-2 flex-shrink-0" />
            <span className="text-sm text-[rgb(var(--text-secondary))]">{item}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default KnowYourSubstancePage;
